import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { AlertTriangle, Play } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

import {
  HttpWebhookTestForm,
  defaultHttpWebhookTestPayload,
  type HttpWebhookTestPayload,
  ScheduleTestForm,
  createDefaultScheduleTestPayload,
  type ScheduleTestPayload,
  BusinessEventTestForm,
  createDefaultBusinessEventTestPayload,
  type BusinessEventTestPayload,
} from './forms';

type TriggerType =
  | 'BusinessEventTrigger'
  | 'HttpWebhookTrigger'
  | 'ScheduleTrigger';

interface TestPayloadDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  triggerType: TriggerType;
  triggerConfig?: Record<string, unknown>;
  onConfirm: (payload: Record<string, unknown>) => void | Promise<void>;
  isTesting?: boolean;
}

export const TestPayloadDialog: React.FC<TestPayloadDialogProps> = ({
  isOpen,
  onOpenChange,
  triggerType,
  triggerConfig,
  onConfirm,
  isTesting = false,
}) => {
  const t = useTranslations();

  const configuredEntityType = triggerConfig?.entityType as string | undefined;
  const configuredEventCase = triggerConfig?.eventCase as string | undefined;

  const [webhookPayload, setWebhookPayload] =
    React.useState<HttpWebhookTestPayload>(defaultHttpWebhookTestPayload);
  const [schedulePayload, setSchedulePayload] =
    React.useState<ScheduleTestPayload>(createDefaultScheduleTestPayload());
  const [businessEventPayload, setBusinessEventPayload] =
    React.useState<BusinessEventTestPayload>(
      createDefaultBusinessEventTestPayload(
        configuredEntityType,
        configuredEventCase,
      ),
    );

  React.useEffect(() => {
    if (isOpen) {
      setWebhookPayload(defaultHttpWebhookTestPayload);
      setSchedulePayload(createDefaultScheduleTestPayload());
      setBusinessEventPayload(
        createDefaultBusinessEventTestPayload(
          configuredEntityType,
          configuredEventCase,
        ),
      );
    }
  }, [isOpen, configuredEntityType, configuredEventCase]);

  const handleClose = () => {
    if (!isTesting) {
      onOpenChange(false);
    }
  };

  const buildPayload = (): Record<string, unknown> => {
    switch (triggerType) {
      case 'BusinessEventTrigger':
        return {
          entityType: businessEventPayload.entityType,
          eventCase: businessEventPayload.eventCase,
          data: businessEventPayload.data,
          timestamp: new Date().toISOString(),
        };
      case 'HttpWebhookTrigger':
        return {
          headers: webhookPayload.headers,
          query: webhookPayload.query,
          body: safeParseJson(webhookPayload.body),
        };
      case 'ScheduleTrigger':
        return {
          scheduledTime: schedulePayload.scheduledTime,
        };
      default:
        return {};
    }
  };

  const handleConfirm = async () => {
    const payload = buildPayload();
    await onConfirm(payload);
  };

  const getTriggerLabel = () => {
    switch (triggerType) {
      case 'BusinessEventTrigger':
        return t.triggerType.BusinessEvent;
      case 'HttpWebhookTrigger':
        return t.triggerType.HttpWebhook;
      case 'ScheduleTrigger':
        return t.triggerType.Schedule;
      default:
        return t.dialogs.test.unknown;
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@TestPayloadDialog"
    >
      <DialogContent
        position="center"
        className="w-full max-w-2xl min-w-[32rem]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Play className="size-5" />
            {t.dialogs.test.title}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto p-4">
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" />
            <div>
              <p className="font-medium text-warning">
                {t.dialogs.test.warning}
              </p>
              <p className="mt-1 text-sm text-warning/80">
                {t.dialogs.test.warningDescription}
              </p>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm text-neutral/60">
              {t.dialogs.test.triggerType}:{' '}
              <span className="font-medium text-neutral">
                {getTriggerLabel()}
              </span>
            </p>
          </div>

          {triggerType === 'HttpWebhookTrigger' && (
            <HttpWebhookTestForm
              value={webhookPayload}
              onChange={setWebhookPayload}
            />
          )}

          {triggerType === 'ScheduleTrigger' && (
            <ScheduleTestForm
              value={schedulePayload}
              onChange={setSchedulePayload}
            />
          )}

          {triggerType === 'BusinessEventTrigger' && (
            <BusinessEventTestForm
              value={businessEventPayload}
              onChange={setBusinessEventPayload}
              configuredEntityType={configuredEntityType}
              configuredEventCase={configuredEventCase}
            />
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onPress={handleClose}
            isDisabled={isTesting}
          >
            {t.dialogs.test.cancel}
          </Button>
          <Button
            variant="solid"
            color="primary"
            onPress={handleConfirm}
            isDisabled={isTesting}
            isLoading={isTesting}
            icon={<Play className="size-4" />}
          >
            {t.dialogs.test.runTest}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};

function safeParseJson(jsonString: string): unknown {
  try {
    return JSON.parse(jsonString || '{}');
  } catch {
    return {};
  }
}
